import React, { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { X, Save, RefreshCw } from 'lucide-react';
import { useCreateContact, useUpdateContact } from '@/hooks/useCompanies';
import { useUsers } from '@/hooks/useUsers';
import type { Contact, CreateContactInput } from '@/types';

interface ContactFormModalProps {
  companyId: string;
  contact: Contact | null;
  onClose: () => void;
}

export function ContactFormModal({ companyId, contact, onClose }: ContactFormModalProps) {
  const isEditing = !!contact;
  const createMutation = useCreateContact(companyId);
  const updateMutation = useUpdateContact(companyId);
  const { data: users } = useUsers();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { errors, isSubmitting },
  } = useForm<CreateContactInput>();

  const watchCommissionType = useWatch({ control, name: 'commissionType' });

  // Populate form when editing
  useEffect(() => {
    if (contact) {
      reset({
        firstName: contact.firstName,
        lastName: contact.lastName,
        title: contact.title || '',
        email: contact.email || '',
        phone: contact.phone || '',
        mobile: contact.mobile || '',
        isPrimary: contact.isPrimary,
        notes: contact.notes || '',
        salesRepUserId: contact.salesRepUserId || '',
        salesRepName: contact.salesRepName || '',
        commissionType: contact.commissionType || ('' as any),
        commissionValue: contact.commissionValue,
        commissionCap: contact.commissionCap,
      });
    }
  }, [contact, reset]);

  const onSubmit = async (data: CreateContactInput) => {
    try {
      // Clean up empty commission fields so they don't overwrite with empty strings
      const cleanData = { ...data };
      if (!cleanData.commissionType) {
        delete cleanData.commissionType;
        delete cleanData.commissionValue;
        delete cleanData.commissionCap;
      }
      if (!cleanData.salesRepUserId) {
        delete cleanData.salesRepUserId;
        delete cleanData.salesRepName;
      }

      if (isEditing && contact) {
        await updateMutation.mutateAsync({ contactId: contact.id, input: cleanData });
      } else {
        await createMutation.mutateAsync(cleanData);
      }
      onClose();
    } catch (error) {
      // Error handled by mutation
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEditing ? 'Edit Contact' : 'Add Contact'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="p-4 space-y-4">
          {/* Name fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                id="firstName"
                type="text"
                className={`input ${errors.firstName ? 'input-error' : ''}`}
                {...register('firstName', { required: 'Required' })}
              />
              {errors.firstName && (
                <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>
              )}
            </div>
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                id="lastName"
                type="text"
                className={`input ${errors.lastName ? 'input-error' : ''}`}
                {...register('lastName', { required: 'Required' })}
              />
              {errors.lastName && (
                <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>
              )}
            </div>
          </div>

          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
              Job Title
            </label>
            <input
              id="title"
              type="text"
              className="input"
              placeholder="e.g., Owner, Sales Manager"
              {...register('title')}
            />
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              id="email"
              type="email"
              className={`input ${errors.email ? 'input-error' : ''}`}
              {...register('email', {
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email',
                },
              })}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
            )}
          </div>

          {/* Phone numbers */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                Office Phone
              </label>
              <input
                id="phone"
                type="tel"
                className="input"
                {...register('phone')}
              />
            </div>
            <div>
              <label htmlFor="mobile" className="block text-sm font-medium text-gray-700 mb-1">
                Mobile
              </label>
              <input
                id="mobile"
                type="tel"
                className="input"
                {...register('mobile')}
              />
            </div>
          </div>

          {/* Primary contact checkbox */}
          <div className="flex items-center gap-2">
            <input
              id="isPrimary"
              type="checkbox"
              className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              {...register('isPrimary')}
            />
            <label htmlFor="isPrimary" className="text-sm text-gray-700">
              Primary contact for this company
            </label>
          </div>

          {/* Notes */}
          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              id="notes"
              rows={2}
              className="input"
              {...register('notes')}
            />
          </div>

          {/* Sales Rep Override */}
          <div className="border-t pt-4">
            <label htmlFor="salesRepUserId" className="block text-sm font-medium text-gray-700 mb-1">
              Sales Rep (Override)
            </label>
            <select
              id="salesRepUserId"
              className="input"
              {...register('salesRepUserId')}
              onChange={(e) => {
                const selectedUser = users?.find((u) => u.id === e.target.value);
                setValue('salesRepUserId', e.target.value);
                setValue('salesRepName', selectedUser?.displayName || '');
              }}
            >
              <option value="">Use company default</option>
              {users
                ?.filter((u) => u.status === 'active')
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.displayName}
                  </option>
                ))}
            </select>
            <input type="hidden" {...register('salesRepName')} />
            <p className="text-xs text-gray-500 mt-1">
              Leave empty to use the company&apos;s default sales rep
            </p>
          </div>

          {/* Commission Override */}
          <div className="border-t pt-4">
            <p className="text-sm font-medium text-gray-700 mb-2">Commission Rate (Override)</p>
            <p className="text-xs text-gray-500 mb-3">
              Leave empty to use the company&apos;s default commission rate
            </p>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="commissionType" className="block text-xs font-medium text-gray-600 mb-1">
                  Type
                </label>
                <select id="commissionType" className="input" {...register('commissionType')}>
                  <option value="">Company default</option>
                  <option value="percentage">Percentage</option>
                  <option value="flat">Flat Amount</option>
                </select>
              </div>
              <div>
                <label htmlFor="commissionValue" className="block text-xs font-medium text-gray-600 mb-1">
                  Value
                </label>
                <input
                  id="commissionValue"
                  type="number"
                  step="0.01"
                  className="input"
                  placeholder={watchCommissionType === 'percentage' ? '0.05 = 5%' : '500'}
                  {...register('commissionValue', { valueAsNumber: true })}
                />
              </div>
            </div>

            {watchCommissionType && (
              <div className="mt-2">
                <label htmlFor="commissionCap" className="block text-xs font-medium text-gray-600 mb-1">
                  Cap (optional)
                </label>
                <input
                  id="commissionCap"
                  type="number"
                  step="0.01"
                  className="input"
                  placeholder="Max commission per project"
                  {...register('commissionCap', { valueAsNumber: true })}
                />
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t">
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn btn-primary flex-1"
            >
              {isSubmitting ? (
                <>
                  <RefreshCw className="animate-spin" size={18} />
                  {isEditing ? 'Saving...' : 'Adding...'}
                </>
              ) : (
                <>
                  <Save size={18} />
                  {isEditing ? 'Save Changes' : 'Add Contact'}
                </>
              )}
            </button>
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
